import {gsap} from "gsap";
import services from "../../../libs/services";
import threeSketch from "../../../libs/three-sketch";
import * as THREE from "three";
import EmitEvent, {
    HT_RESIZE,
    OBSERVER_HEIGHT_CHANGED,
    PAGE_LOADED,
} from "../../../libs/emit-event";
import {imgSrcOriginal, isScreenMobile, isScreenPc, ThreeJsGetX, ThreeJsGetY} from "../../../libs/helper";
import f from "./f.glsl";
import v from "./v.glsl";
import AnimationChars from "../../animation/chars/chars";
import AnimationFade from "../../animation/fade/fade";

export class ProjectItem {

    constructor({el, elHeading, addGroup, uniforms, index, indexActive}) {
        this.DOM = {el, elHeading};
        this.DOM.thumbnail = this.DOM.el.querySelector('.projectPageItem_thumbnail');
        this.prop = {top: 0, translate: 0};
        this.addGroup = addGroup;
        this.index = index;
        this.indexOnHold = indexActive;

        this.uniforms = {
            ...uniforms, ...{
                u_texture: {value: null},
                u_size: {value: new THREE.Vector2()},
                u_res: {value: new THREE.Vector2(services.winSize.width, services.winSize.height)},
                u_radius: {value: .009},
            }
        };

        this.style = {
            marginRatio: isScreenPc() ? 420 / 1920 : 100 / 768,
            margin: 0,
            scaleX: 0
        };

        this.persenElement = 0.648;

        this.animations = {
            heading: new AnimationChars(this.DOM.elHeading.querySelector('.js-projectsPage_names_heading'), {type: '2d'}),
            year: new AnimationFade(this.DOM.elHeading.querySelector('.js-projectsPage_names_meta_year')),
            button: new AnimationFade(this.DOM.elHeading.querySelector('.js-projectsPage_names_meta_button')),
        };

        if (this.index === this.indexOnHold) {
            this.DOM.elHeading.style.zIndex = '1';
            this.DOM.elHeading.style.pointerEvents = 'auto';
        } else {
            this.DOM.elHeading.style.zIndex = '0';
            this.DOM.elHeading.style.pointerEvents = 'none';
        }

        this.pageLoaded = this.pageLoaded.bind(this);
        this.handleScrolling = this.handleScrolling.bind(this);
        this.pageResize = this.pageResize.bind(this);

        this.init();
        this.bindEvent();
    }

    getHeight() {
        return services.winSize.height * this.persenElement;
    }

    getWidth() {
        return this.getHeight() * (91 / 70);
    }

    getProp() {

        const scrollTop = Math.abs(services.smooth_scrolling_instance);
        const {top, width, height, left} = this.DOM.thumbnail.getBoundingClientRect();
        this.prop.top = top + scrollTop;
        this.prop.left = left;
        this.prop.width = width;
        this.prop.height = height;
        this.uniforms.u_size.value = {
            x: this.prop.width,
            y: this.prop.height
        };

        this.style.margin = this.style.marginRatio * services.winSize.width;
    }

    animationTextIn() {

        this.DOM.elHeading.style.zIndex = '1';
        this.DOM.elHeading.style.pointerEvents = 'auto';

        this.animations.heading.animationIn();
        this.animations.year.animationIn(.25);
        this.animations.button.animationIn(.3);
    }

    animationTextOut() {

        this.DOM.elHeading.style.zIndex = '0';
        this.DOM.elHeading.style.pointerEvents = 'none';

        this.animations.heading.animationOutBottomAll();
        this.animations.year.animationOut();
        this.animations.button.animationOut();
    }

    scaleOut() {
        gsap.killTweensOf(this.style);
        gsap.to(this.style, {scaleX: 0, ease: 'power3.out', duration: 1.2});
    }

    scaleIn(index) {
        const valueScale = index * this.style.margin;
        gsap.killTweensOf(this.style);
        gsap.to(this.style, {scaleX: valueScale, ease: 'power3.out', duration: .8});
    }

    getMargin() {
        return (this.style.margin * this.index) + this.style.scaleX;
    }

    getFocus() {
        return (((this.style.margin * this.index) + this.prop.left + (this.prop.width / 2)) - (services.winSize.width / 2));
    }

    init() {
        const image = this.DOM.el.querySelector('img');
        const src = imgSrcOriginal(image);
        threeSketch.loadTexture({src}).then(this.initTexture.bind(this));
    }

    initTexture(texture) {
        this.getProp();

        this.uniforms.u_texture.value = texture;
        // this.uniforms.u_texture.value.magFilter = THREE.NearestFilter;
        this.uniforms.u_texture.value.minFilter = THREE.LinearFilter;

        const geo = new THREE.PlaneBufferGeometry(1, 1, 20, 20);
        const mat = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            fragmentShader: f,
            vertexShader: v,
            transparent: true,
        });
        this.mesh = new THREE.Mesh(geo, mat);
        this.addGroup(this.mesh);

        this.DOM.el.style.visibility = 'hidden';
        this.setSize();
    }

    setSize() {
        if (this.mesh) {
            this.mesh.scale.set(this.prop.width, this.prop.height, 1);
            this.mesh.position.set(ThreeJsGetX(this.prop.left + this.getMargin(), this.prop.width), ThreeJsGetY(this.prop.top, this.prop.height), 1);
        }
    }

    dragging(x) {
        if (this.mesh) {
            const left = this.prop.left + this.getMargin() + x;
            this.mesh.position.x = ThreeJsGetX(left, this.prop.width);
        }
    }

    draggingTestActive(x, triggerActive) {
        if (this.mesh) {
            const left = this.prop.left + this.getMargin() + x;
            const disLeft = left + this.prop.width;
            if (left < triggerActive.triggerStart && disLeft > triggerActive.triggerEnd) {
                return true;
            }
            return false;
        }
    }

    pageLoaded() {
        this.getProp();
        this.setSize();
    }

    handleScrolling(instance) {
        if (this.mesh) {
            const disTop = (this.prop.top + instance);
            this.mesh.position.y = ThreeJsGetY(disTop, this.prop.height);
        }
    }

    pageResize() {
        this.style.marginRatio = isScreenPc() ? 420 / 1920 : 100 / 768;
        this.uniforms.u_res.value = new THREE.Vector2(services.winSize.width, services.winSize.height);

        this.getProp();
        this.setSize();
    }

    bindEvent() {

        EmitEvent.on(HT_RESIZE, this.pageResize);
        EmitEvent.on(OBSERVER_HEIGHT_CHANGED, () => {
            this.pageResize();
        });
        EmitEvent.on(PAGE_LOADED, this.pageLoaded);
    }

    removeEvent() {
        EmitEvent.off(HT_RESIZE, this.pageResize);
        EmitEvent.off(OBSERVER_HEIGHT_CHANGED, this.pageResize);
        EmitEvent.off(PAGE_LOADED, this.pageLoaded);
    }
}